<template>
    <div class="fill-height">
        <v-hover
            v-if="variant === 'box'"
            v-slot="{ isHovering, props }"
        >
            <v-card
                class="product-card text-center fill-height d-flex flex-column rounded-0"
                :elevation="isHovering && !disableHover ? 2 : 0"
                v-bind:class="cssClass"
                v-bind="props"
                @click.stop.middle="visit(actualProductVariant?.url || product.url, true)"
                @click="router.visit(actualProductVariant?.url || product.url, {onStart: handleLink})"
            >
                <tag :flags="product.flags" />
                <div>
                    <v-responsive :aspect-ratio="1" class="position-relative">
                        <v-img
                            aspect-ratio="1"
                            :src="product.main_image?.url"
                            :alt="product.main_image?.alt"
                            width="100%"
                            class="bg-white"
                        >
                        </v-img>
                        <transition name="fade-transition" v-if="product.other_image">
                            <v-img
                                aspect-ratio="1"
                                :src="product.other_image?.url"
                                :alt="product.other_image?.alt"
                                class="image2 bg-white"
                                width="100%"
                                v-show="isHovering"
                            >
                            </v-img>
                        </transition>
                        <v-overlay
                            :model-value="isHovering"
                            contained
                            scroll-strategy="reposition"
                            scrim="#036358"
                            class="align-center justify-center hover-menu"
                            :class="{'is-hovering' : isHovering}"
                        >
                            <div class="d-flex flex-column justify-space-between h-100">
                                <div class="buttons d-flex flex-column pr-3 pt-3 align-end">
                                    <div v-if="product.variants_count === 0 || actualProductVariant?.id"
                                         class="text-right buttons-button__wrap">
                                        <div
                                            class="c-pill rounded-circle position-relative d-inline-flex align-center pa-1"
                                            @click.prevent.stop="solveComparator(product.id, actualProductVariant?.id)">
                                            <v-badge :class="{'badge-hide': !isComparing}" offset-y="-5" offset-x="-5"
                                                     dot color="green">
                                                <v-icon size="x-small" :icon="mdiCompareHorizontal" />
                                            </v-badge>
                                            <span class="text-caption c-pill--content">
                                                {{ isComparing ? $t('eshop::comparator.remove') : $t('eshop::comparator.add')
                                                }}
                                            </span>
                                            <v-tooltip
                                                v-if="comparatorTooltip.message"
                                                activator="parent"
                                                location="bottom"
                                                v-model="comparatorTooltip.model"
                                                @update:model-value="$event ? null : comparatorTooltip.message = ''"
                                            >
                                                {{ comparatorTooltip.message }}
                                            </v-tooltip>
                                        </div>
                                    </div>
                                    <slot name="actions"></slot>
                                </div>
                                <template v-if="catalog && $page.props.eshopEnabled">
                                    <CartButtonCatalog
                                        v-if="!product.info_product && (product.variants_count === 0 && product.on_stock) || actualProductVariant?.id"
                                        :text="$tChoice('eshop::base-product.add_to_cart', $vuetify.display.smAndDown ? 0 : 1)"
                                        :icon="mdiCart" :isHovering="isHovering"
                                        @click.stop.prevent="addToOrder(product)">
                                    </CartButtonCatalog>
                                    <CartButtonCatalog
                                        v-else-if="product.variants_count !== 0"
                                        :text="$tChoice('eshop::base-product.choose_a_variant', $vuetify.display.smAndDown ? 0 : 11)"
                                        :icon="mdiFormatListBulletedType"
                                        :isHovering="isHovering"
                                    >
                                    </CartButtonCatalog>
                                </template>
                            </div>
                        </v-overlay>
                    </v-responsive>
                </div>
                <v-card-text class="d-flex flex-column">
                    <div>
                        <Link
                            v-if="!!product.default_group"
                            :href="product.default_group.url"
                            class="text-black d-inline-block to-warning mb-1"
                        >
                            <div class="font-weight-thin">
                                {{ product.default_group.name }}
                            </div>
                        </Link>
                    </div>
                    <div class="flex-grow-1 d-flex flex-column justify-space-between">
                        <Link :href="actualProductVariant?.url || product.url"
                              class="product-name font-weight-bold mb-1" :onStart="handleLink">
                            {{ actualProductVariant?.name || product.name }}
                        </Link>
                        <div class="font-weight-bold to-warning__static" v-if="$page.props.eshopEnabled">
                            <template v-if="hasActionPrice">
                                <div class="d-flex flex-column">
                                    <div>{{ actualProductVariant?.price || product.price }}</div>
                                    <div class="text-caption text-decoration-line-through">{{ actualProductVariant?.base_price || product.base_price }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                {{ actualProductVariant?.price || product.price }}
                            </template>
                        </div>
                        <slot name="more_info"></slot>
                        <primary-variant v-if="!actualProductVariant?.id && product.variants_count > 0"
                                         :variants="product.primary_variants"
                                         class="mt-2" />
                        <template v-if="!catalog && $page.props.eshopEnabled">
                            <CartButton v-if="!product.info_product && product.variants_count === 0 && product.on_stock"
                                        :text="$tChoice('eshop::base-product.add_to_cart',$vuetify.display.smAndDown ? 0 : 1)"
                                        :icon="mdiCart"
                                        :isHovering="isHovering"
                                        @click.stop.prevent="addToOrder(product)"
                            >
                            </CartButton>
                            <CartButton v-else-if="product.variants_count !== 0"
                                        :text="$tChoice('eshop::base-product.choose_a_variant', $vuetify.display.smAndDown ? 0 : 1)"
                                        :icon="mdiFormatListBulletedType"
                                        :isHovering="isHovering">
                            </CartButton>
                            <div v-else class="flex-fill"></div>
                        </template>
                    </div>
                </v-card-text>
            </v-card>
        </v-hover>
        <v-hover
            v-else-if="variant === 'list'"
            v-slot="{ isHovering, props }"
        >
            <v-card
                class="product-card text-center rounded-0"
                elevation="0"
                v-bind:class="cssClass"
                v-bind="props"

            >
                <tag :to-line="$vuetify.display.width < 520" :flags="product.flags" />
                <v-row align="center">
                    <v-col cols="6" md="3" order="0">
                        <Link :href="product.url" :onStart="handleLink">
                            <v-responsive :aspect-ratio="1" class="position-relative" width="100%">
                                <v-img
                                    aspect-ratio="1"
                                    :src="product.main_image?.url"
                                    :alt="product.main_image?.alt"
                                    width="100%"
                                    class="bg-white"
                                >
                                </v-img>
                                <transition name="fade-transition" v-if="product.other_image">
                                    <v-img
                                        aspect-ratio="1"
                                        :src="product.other_image?.url"
                                        :alt="product.other_image?.alt"
                                        width="100%"
                                        class="image2 bg-white"
                                        v-show="isHovering"
                                    >
                                    </v-img>
                                </transition>
                            </v-responsive>
                        </Link>
                    </v-col>
                    <v-col order="2" md="6" order-md="1" class="text-left">
                        <v-card-text>
                            <div>
                                <Link :href="product.default_group.url"
                                      class="text-black d-inline-block to-warning mb-1" v-if="!!product.default_group">
                                    <div class="font-weight-thin">
                                        {{ product.default_group.name }}
                                    </div>
                                </Link>
                            </div>
                            <div>
                                <Link :href="product.url" class="text-black d-inline-block to-warning"
                                      :onStart="handleLink">
                                    <div class="product-name font-weight-bold">
                                        {{ product.name }}
                                    </div>
                                </Link>
                            </div>
                            <p class="c-perex mt-2 font-weight-thin" v-if="product.annotation"
                               v-html="product.annotation" />
                            <primary-variant v-if="product.variants_count > 0" :variants="product.primary_variants"
                                             class="mt-4 justify-start" />
                        </v-card-text>
                    </v-col>
                    <v-col cols="6" md="3" order="1" order-md="2" class="text-left">
                        <v-card-text>
                            <div class="to-warning__static">
                                <template v-if="hasActionPrice">
                                    <div class="d-flex flex-column">
                                        <div>{{ product.price }}</div>
                                        <div class="text-caption text-decoration-line-through">{{ product.base_price
                                            }}
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    {{ actualProductVariant?.price || product.price }}
                                </template>
                            </div>
                            <div class="d-flex flex-column mt-3">
                                <div
                                    v-if="product.variants_count === 0 || actualProductVariant?.id"
                                    class="d-inline-flex align-center"
                                >
                                    <v-badge :class="{'badge-hide': !isComparing}" dot
                                             color="green">
                                        <v-btn
                                            size="x-small"
                                            variant="plain"
                                            class="to-warning"
                                            :prepend-icon="mdiCompareHorizontal"
                                            @click.prevent.stop="solveComparator(product.id, actualProductVariant?.id)"
                                        >
                                            {{ isComparing ? $t('eshop::comparator.remove') : $t('eshop::comparator.add')
                                            }}
                                            <v-tooltip
                                                v-if="comparatorTooltip.message"
                                                activator="parent"
                                                location="bottom"
                                                v-model="comparatorTooltip.model"
                                                @update:model-value="$event ? null : comparatorTooltip.message = ''"
                                            >
                                                {{ comparatorTooltip.message }}
                                            </v-tooltip>
                                        </v-btn>
                                    </v-badge>
                                </div>
                            </div>
                            <cart-button-list
                                v-if="product.variants_count === 0 && product.on_stock && $page.props.eshopEnabled"
                                :text="$tChoice('eshop::base-product.add_to_cart',$vuetify.display.xs ? 0 : 1)"
                                :icon="mdiCart"
                                @click.stop.prevent="addToOrder(product)"
                            >
                            </cart-button-list>
                            <Link v-else-if="product.variants_count !== 0 && $page.props.eshopEnabled" :href="product.url" :onStart="handleLink">
                                <cart-button-list
                                    :text="$tChoice('eshop::base-product.choose_a_variant',$vuetify.display.xs ? 0 : 1)"
                                    :icon="mdiFormatListBulletedType">

                                </cart-button-list>
                            </Link>
                        </v-card-text>
                    </v-col>
                </v-row>
            </v-card>
        </v-hover>
    </div>
</template>

<script setup>
import { Link, router, usePage } from '@inertiajs/vue3';
import { computed, defineAsyncComponent, inject, ref } from 'vue';
import PrimaryVariant from './PrimaryVariant.vue';
import axios from 'axios';
import Tag from './Tag.vue';
import { mdiBinoculars, mdiCart, mdiCompareHorizontal, mdiFormatListBulletedType } from '@mdi/js';
import { addEvent, generateItem } from '../../../gtm';
import { useCartStore } from '../store/cart';
import { useComparatorStore } from '../store/comparator.js';
import pinia from '../../../plugins/pinia';
import CartButton from './CartButton.vue';
import CartButtonCatalog from './CartButtonCatalog.vue';
import CartButtonList from './CartButtonList.vue';

const cartStore = useCartStore(pinia);
const comparatorStore = useComparatorStore(pinia);

const ProductQuickDetail = defineAsyncComponent(() => import('./ProductQuickDetail.vue'));

const props = defineProps({
    product: Object,
    actualProductVariant: Object,
    cssClass: [String, Array, Object],
    disableHover: Boolean,
    variant: {
        type: String,
        default: 'box',
    },
    catalog: {
        type: Boolean,
        default: true,
    },
});
const dialog = ref(false);
const quickProduct = ref({});
const route = inject('route');
const toAdd = ref(props.product.on_stock ? 1 : 0);
const openDialog = (id) => {
    axios.post(route('product.ajax'), { id }).then((resp) => {
        quickProduct.value = resp.data;
        dialog.value = true;
    });
};

const hasActionPrice = computed(() => {
    let has = false;
    let base, action;

    if (props.actualProductVariant?.id) {
        base = parseFloat(props.actualProductVariant.base_price.replace(/ /g, ''));
        action = parseFloat(props.actualProductVariant.price.replace(/ /g, ''));
    } else {
        base = parseFloat(props.product.base_price.replace(/ /g, ''));
        action = parseFloat(props.product.price.replace(/ /g, ''));
    }

    if (action < base) {
        has = true;
    }
    return has;
});

const addToOrder = (product, count) => {
    // eslint-disable-next-line no-undef
    if (typeof count === 'undefined') {
        count = toAdd.value;
    }
    const options = {
        id_product: product.id, count,
    };
    if (props.actualProductVariant?.id) {
        options.id_variant = props.actualProductVariant.id;
    }
    router.post(route('order.addProduct'), options, {
        preserveScroll: true,
        onSuccess: () => {
            const item = cartStore.cartItems.find((item) => item.id_products == product.id);
            if (item) {
                const price = parseFloat(item.nice_price.replace(/\s/g, '').replace(/,/g, '.'));
                fireGaEvent(props.product, 'add_to_cart', {
                    currency: usePage().props.currency,
                    value: (price * count).toFixed(2),
                }, { quantity: count, price: price.toFixed(2) });
            }
        },
    });
};

const fireGaEvent = (product, event, attributes, itemAttributes = null) => {
    addEvent({
        event, ecommerce: {
            ...attributes,
            items: [
                generateItem(product, itemAttributes),
            ],
        },
    });
};

const handleLink = () => {
    if (usePage().props.group) {
        fireGaEvent(props.product, 'select_item', {
            item_list_id: usePage().props.group.id,
            item_list_name: usePage().props.group.name,
        });
    }
};

const comparatorTooltip = ref({
    model: false,
    message: '',
});

const isComparing = computed(() => {
    const data = comparatorStore.data.data;
    if (!data.length) return false;
    const isVariant = !!props.actualProductVariant?.id;
    const found = data.find(item => {
        return item.type === (isVariant ? 'variant' : 'product') && item.id === (isVariant ? props.actualProductVariant.id : props.product.id);
    });
    return !!found;
});

const solveComparator = (id, variantId) => {
    const add = !isComparing.value;
    const query = [id];
    if (variantId) {
        query.push(variantId);
    }
    const _route = add ? route('comparator.add', query) : route('comparator.remove', query);
    axios[add ? 'put' : 'delete'](_route).then(resp => {
        comparatorStore.data.data = resp.data.data;
        comparatorStore.data.count = resp.data.count;
        comparatorTooltip.value.model = true;
        comparatorTooltip.value.message = resp.data.message;
    });
};

const visit = (url, inNewTab = false) => {
    if (inNewTab) {
        handleLink();
        newTab(url);
    } else {
        router.visit(url, {onStart: handleLink});
    }
};

const newTab = (url) => {
    window.open(url);
}


</script>

<style scoped lang="scss">
.product-name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: inherit;
}

.image2 {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 1;
}

::v-deep(.hover-menu) {
    .v-overlay__content {
        width: 100%;
        height: 100%;
    }
}

.buttons {
    opacity: 0;
    transition: .3s ease-in-out;
    transition-property: box-shadow, transform, opacity, background, border-radius;
    transform: translate3d(-10%, 0, 0);
    gap: .3em;
}

.hover-menu {
    &.is-hovering {
        .buttons {
            opacity: 1;
            transform: translate3d(0, 0, 0);
        }
    }
}

.slide-up {
    transform: translate3d(0, 120%, 0);

    &.is-hovering {
        transform: translate3d(0, 0, 0);
    }
}

:deep(.product-card) {
    &:hover {
        > .v-card__overlay {
            opacity: 0;
        }
    }
}
</style>

<style lang="scss">

.c-pill--content {
    transition-property: transform, opacity, visibility;
    transition-duration: .35s;
    transition-timing-function: ease;
    position: absolute;
    background-color: rgb(var(--v-theme-cis-accent-color));
    height: 100%;
    right: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    padding-inline: .6em .2em;
    color: white;
    opacity: 0;
    visibility: hidden;
    white-space: nowrap;
    border-top-left-radius: 30px;
    border-bottom-left-radius: 30px;
    transform: translate3d(15px, 0, 0);
    z-index: -1;
}

.c-pill {
    transition-property: background-color, border-radius;
    transition-duration: .35s;
    transition-timing-function: ease;
    background-color: white;
}

.buttons-button__wrap {
    &:hover, &:focus, &:active {
        .c-pill {
            background-color: rgb(var(--v-theme-cis-accent-color));
            border-top-left-radius: 0 !important;
            border-bottom-left-radius: 0 !important;

            .v-icon {
                color: white;
            }
        }

        .c-pill--content {
            opacity: 1;
            visibility: visible;
            transform: translate3d(0, 0, 0);
        }
    }
}

.badge-hide {
    .v-badge__badge {
        display: none;
    }
}
</style>
